import React, { useState } from "react";
import { BsFillBuildingFill } from "react-icons/bs";
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiToggleLeft,
  FiToggleRight,
  FiRefreshCw,
  FiLink,
  FiChevronDown,
  FiChevronRight,
  FiUsers,
} from "react-icons/fi";
import { MdMiscellaneousServices } from "react-icons/md";
import { useBrandManagement } from "../hooks/useBrandManagement";
import { useServicesManagement } from "../hooks/useServicesManagement";
import { useBrandServicesManagement } from "../hooks/useBrandServicesManagement";
import { tenantService } from "../../../shared/services/apis/tenant";
import BrandModal from "../components/BrandModal";
import ServiceModal from "../components/ServiceModal";
import AssignServiceModal from "../components/AssignServiceModal";
import RiskParameterModal from "../components/MlQueryModel";
import ConfirmDialog from "../components/ConfirmDialog";
import type {
  Brand,
  CreateBrandData,
  UpdateBrandData,
  Service,
  CreateServiceData,
  UpdateServiceData,
  AssignServiceToBrandData,
  Tenant,
} from "shared/types";
import { useMlQueries } from "../hooks/useMlQueries";
//MlQueryCategory
export const MlQueryCategory = {
  RISK_PARAMETERS: "RISK_PARAMETERS",
  BANK_RISK_SCORE: "BANK_RISK_SCORE",
  KYC: "KYC",
} as const;
export type MlQueryCategory = typeof MlQueryCategory[keyof typeof MlQueryCategory];
const SuperAdmin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    "brands" | "services" | "brand-services"
  >("brands");

  // State for managing expanded brands and their tenants
  const [expandedBrands, setExpandedBrands] = useState<Set<string>>(new Set());
  const [brandTenants, setBrandTenants] = useState<Record<string, Tenant[]>>(
    {}
  );
  const [loadingTenants, setLoadingTenants] = useState<Set<string>>(new Set());

  // State for managing expanded risk parameters
  const [expandedRiskParams, setExpandedRiskParams] = useState<Set<string>>(
    new Set()
  );

  // State for risk parameter filters
  const [riskParameterFilters, setRiskParameterFilters] = useState<
    Record<
      string,
      {
        status?: string;
        type?: string;
        sortBy?: "created" | "status" | "type" | "description";
        sortOrder?: "asc" | "desc";
      }
    >
  >({});

  // Brand management state
  const {
    brands,
    loading: brandLoading,
    error: brandError,
    creating: brandCreating,
    updating: brandUpdating,
    deleting: brandDeleting,
    createBrand,
    updateBrand,
    activateBrand,
    deactivateBrand,
    deleteBrand,
    refreshBrands,
  } = useBrandManagement();

  // Services management state
  const {
    services,
    loading: servicesLoading,
    error: servicesError,
    creating: serviceCreating,
    updating: serviceUpdating,
    deleting: serviceDeleting,
    createService,
    updateService,
    toggleServiceStatus,
    deleteService,
    refreshServices,
  } = useServicesManagement();

  // Brand services management state
  const {
    brandServices,
    availableServices,
    loading: brandServicesLoading,
    error: brandServicesError,
    creating: brandServiceCreating,
    updating: brandServiceUpdating,
    deleting: brandServiceDeleting,
    assignServiceToBrand,
    unassignServiceFromBrand,
    toggleBrandServiceStatus,
    refreshBrandServices,
    fetchAvailableServices,
  } = useBrandServicesManagement();

  // Risk parameters management state
  const {
    riskParameters,
    loading: riskParametersLoading,
    error: riskParametersError,
    creating: riskParameterCreating,
    updating: riskParameterUpdating,
    createRiskParameter,
    updateRiskParameter,
    deleteRiskParameter,
    deleteAllRiskParameters,
    refreshRiskParameters,
  } = useMlQueries();

  // Modal states
  const [brandModal, setBrandModal] = useState<{
    isOpen: boolean;
    brand: Brand | null;
  }>({ isOpen: false, brand: null });

  const [serviceModal, setServiceModal] = useState<{
    isOpen: boolean;
    service: Service | null;
  }>({ isOpen: false, service: null });

  const [assignServiceModal, setAssignServiceModal] = useState<{
    isOpen: boolean;
    selectedBrand: Brand | null;
  }>({ isOpen: false, selectedBrand: null });

  const [riskParameterModal, setRiskParameterModal] = useState<{
    isOpen: boolean;
    riskParameter:
      | import("../../../shared/services/apis/mlQueries").MlQuery
      | null;
  }>({ isOpen: false, riskParameter: null });

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: "danger" | "warning" | "info";
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const tabs = [
    { id: "brands", label: "Brand Management", icon: BsFillBuildingFill },
    { id: "services", label: "Services", icon: MdMiscellaneousServices },
    { id: "brand-services", label: "Brand Services", icon: FiLink },
  ];

  // Brand management handlers
  const handleCreateBrand = () => {
    setBrandModal({ isOpen: true, brand: null });
  };

  // Function to toggle brand expansion and fetch tenants
  const handleToggleBrandExpansion = async (brandId: string) => {
    const isExpanded = expandedBrands.has(brandId);
    const newExpandedBrands = new Set(expandedBrands);

    if (isExpanded) {
      newExpandedBrands.delete(brandId);
    } else {
      newExpandedBrands.add(brandId);

      // Fetch tenants if not already fetched
      if (!brandTenants[brandId]) {
        setLoadingTenants(new Set([...loadingTenants, brandId]));
        try {
          const tenants = await tenantService.getTenantsByBrand(brandId);
          setBrandTenants((prev) => ({
            ...prev,
            [brandId]: tenants,
          }));
        } catch (error) {
          console.error("Failed to fetch tenants for brand:", error);
        } finally {
          setLoadingTenants((prev) => {
            const newSet = new Set(prev);
            newSet.delete(brandId);
            return newSet;
          });
        }
      }
    }

    setExpandedBrands(newExpandedBrands);
  };

  // Function to toggle risk parameter expansion
  const handleToggleRiskParamsExpansion = (tenantId: string) => {
    const isExpanded = expandedRiskParams.has(tenantId);
    const newExpandedRiskParams = new Set(expandedRiskParams);

    if (isExpanded) {
      newExpandedRiskParams.delete(tenantId);
    } else {
      newExpandedRiskParams.add(tenantId);
    }

    setExpandedRiskParams(newExpandedRiskParams);
  };

  // Function to update risk parameter filters for a specific tenant
  const updateRiskParameterFilter = (
    tenantId: string,
    filterKey: string,
    value: string
  ) => {
    setRiskParameterFilters((prev) => ({
      ...prev,
      [tenantId]: {
        ...prev[tenantId],
        [filterKey]: value === "all" ? undefined : value,
      },
    }));
  };

  // Function to get filtered and sorted risk parameters
  const getFilteredRiskParameters = (
    tenantRiskParameters: any[],
    tenantId: string
  ) => {
    const filters = riskParameterFilters[tenantId] || {};
    let filtered = [...tenantRiskParameters];

    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter((rp) => rp.responseStatus === filters.status);
    }

    // Apply type filter
    if (filters.type) {
      filtered = filtered.filter((rp) => rp.type === filters.type);
    }

    // Apply sorting
    if (filters.sortBy) {
      filtered.sort((a, b) => {
        let aValue = a[filters.sortBy!];
        let bValue = b[filters.sortBy!];

        if (filters.sortBy === "created") {
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
        }

        if (aValue < bValue) return filters.sortOrder === "desc" ? 1 : -1;
        if (aValue > bValue) return filters.sortOrder === "desc" ? -1 : 1;
        return 0;
      });
    }

    return filtered;
  };

  const handleEditBrand = (brand: Brand) => {
    setBrandModal({ isOpen: true, brand });
  };

  const handleSaveBrand = async (data: CreateBrandData | UpdateBrandData) => {
    if (brandModal.brand) {
      await updateBrand(brandModal.brand.id, data as UpdateBrandData);
    } else {
      await createBrand(data as CreateBrandData);
    }
  };

  const handleToggleBrandStatus = async (brand: Brand) => {
    const action = brand.isActive ? "deactivate" : "activate";
    setConfirmDialog({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Brand`,
      message: `Are you sure you want to ${action} "${brand.name}"? This will ${
        brand.isActive ? "disable" : "enable"
      } all associated services and users.`,
      onConfirm: async () => {
        try {
          if (brand.isActive) {
            await deactivateBrand(brand.id);
          } else {
            await activateBrand(brand.id);
          }
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        } catch (error) {
          console.error(`Failed to ${action} brand:`, error);
        }
      },
      type: brand.isActive ? "warning" : "info",
    });
  };

  const handleDeleteBrand = (brand: Brand) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Brand",
      message: `Are you sure you want to permanently delete "${brand.name}"? This action cannot be undone and will remove all associated data.`,
      onConfirm: async () => {
        try {
          await deleteBrand(brand.id);
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        } catch (error) {
          console.error("Failed to delete brand:", error);
        }
      },
      type: "danger",
    });
  };

  // Service management handlers
  const handleCreateService = () => {
    setServiceModal({ isOpen: true, service: null });
  };

  const handleEditService = (service: Service) => {
    setServiceModal({ isOpen: true, service });
  };

  const handleSaveService = async (
    data: CreateServiceData | UpdateServiceData
  ) => {
    if (serviceModal.service) {
      await updateService(serviceModal.service.id, data as UpdateServiceData);
    } else {
      await createService(data as CreateServiceData);
    }
  };

  const handleToggleServiceStatus = async (service: Service) => {
    const action = service.isActive ? "deactivate" : "activate";
    setConfirmDialog({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Service`,
      message: `Are you sure you want to ${action} "${service.name}"? This will affect all brands using this service.`,
      onConfirm: async () => {
        try {
          await toggleServiceStatus(service.id);
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        } catch (error) {
          console.error(`Failed to ${action} service:`, error);
        }
      },
      type: service.isActive ? "warning" : "info",
    });
  };

  const handleDeleteService = (service: Service) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Service",
      message: `Are you sure you want to permanently delete "${service.name}"? This action cannot be undone and will remove all brand assignments.`,
      onConfirm: async () => {
        try {
          await deleteService(service.id);
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        } catch (error) {
          console.error("Failed to delete service:", error);
        }
      },
      type: "danger",
    });
  };

  // Brand service management handlers
  const handleAssignService = (brand?: Brand) => {
    setAssignServiceModal({ isOpen: true, selectedBrand: brand || null });
    if (brand) {
      fetchAvailableServices(brand.id);
    }
  };

  const handleSaveAssignment = async (data: AssignServiceToBrandData) => {
    await assignServiceToBrand(data);
    refreshBrandServices();
  };

  const handleBrandChangeInModal = (brandId: string) => {
    fetchAvailableServices(brandId);
  };

  const handleUnassignService = (brandService: any) => {
    const brandName = brandService.brand?.name || "Unknown Brand";
    const serviceName = brandService.service?.name || "Unknown Service";

    setConfirmDialog({
      isOpen: true,
      title: "Unassign Service",
      message: `Are you sure you want to unassign "${serviceName}" from "${brandName}"? This will affect all tenants using this service.`,
      onConfirm: async () => {
        try {
          await unassignServiceFromBrand(
            brandService.brandId,
            brandService.serviceId
          );
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        } catch (error) {
          console.error("Failed to unassign service:", error);
        }
      },
      type: "warning",
    });
  };

  const handleToggleBrandServiceStatus = (brandService: any) => {
    const action = brandService.isActive ? "deactivate" : "activate";
    const brandName = brandService.brand?.name || "Unknown Brand";
    const serviceName = brandService.service?.name || "Unknown Service";

    setConfirmDialog({
      isOpen: true,
      title: `${
        action.charAt(0).toUpperCase() + action.slice(1)
      } Brand Service`,
      message: `Are you sure you want to ${action} "${serviceName}" for "${brandName}"?`,
      onConfirm: async () => {
        try {
          await toggleBrandServiceStatus(brandService.id);
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        } catch (error) {
          console.error(`Failed to ${action} brand service:`, error);
        }
      },
      type: brandService.isActive ? "warning" : "info",
    });
  };

  // Risk parameter management handlers
  const handleCreateRiskParameter = (
    tenant: Tenant,
    category: string = "RISK_PARAMETERS"
  ) => {
    setRiskParameterModal({
      isOpen: true,
      riskParameter: null,
    });
    // Pre-fill the tenant in the modal
    setSelectedTenant(tenant);
    setSelectedCategory(category);
  };

  const handleEditRiskParameter = (
    riskParameter: import("../../../shared/services/apis/mlQueries").MlQuery
  ) => {
    setRiskParameterModal({ isOpen: true, riskParameter });
  };

  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [selectedCategory, setSelectedCategory] =
    useState<string>("RISK_PARAMETERS");

  const handleSaveRiskParameter = async (data: any, category?: string) => {
    try {
      if (riskParameterModal.riskParameter) {
        // When editing, set status to PENDING and response to undefined
        const updateData = {
          ...data,
          responseStatus: "PENDING" as const,
          response: undefined,
        };
        await updateRiskParameter(
          riskParameterModal.riskParameter.id,
          updateData
        );
      } else {
        await createRiskParameter(data, category || selectedCategory);
      }
      setRiskParameterModal({ isOpen: false, riskParameter: null });
      await refreshRiskParameters();
    } catch (error) {
      console.error("Failed to save risk parameter:", error);
      throw error; // Let the modal handle the error display
    }
  };

  const handleDeleteRiskParameter = async (id: string) => {
    try {
      await deleteRiskParameter(id);
      setRiskParameterModal({ isOpen: false, riskParameter: null });
      await refreshRiskParameters();
    } catch (error) {
      console.error("Failed to delete risk parameter:", error);
    }
  };

  const handleDeleteRiskParameterInline = async (id: string) => {
    try {
      await deleteRiskParameter(id);
      await refreshRiskParameters();
    } catch (error) {
      console.error("Failed to delete risk parameter:", error);
    }
  };

  const handleDeleteAllRiskParameters = async (
    tenantId: string,
    category?: string
  ) => {
    try {
      await deleteAllRiskParameters(tenantId, category);
      await refreshRiskParameters();
    } catch (error) {
      console.error("Failed to delete all risk parameters:", error);
    }
  };

  const loading =
    brandLoading ||
    servicesLoading ||
    brandServicesLoading ||
    riskParametersLoading;
  const currentError =
    brandError || servicesError || brandServicesError || riskParametersError;

  // Get all available tenants for the modal
  const allTenants = React.useMemo(() => {
    const tenantMap = new Map<string, Tenant>();

    // Add tenants from brandTenants
    for (const tenantList of Object.values(brandTenants)) {
      for (const tenant of tenantList) {
        tenantMap.set(tenant.id, tenant);
      }
    }

    // Add tenants from brands.tenants
    for (const brand of brands) {
      if (brand.tenants) {
        for (const tenant of brand.tenants) {
          tenantMap.set(tenant.id, tenant);
        }
      }
    }

    return Array.from(tenantMap.values());
  }, [brandTenants, brands]);

  if (loading && brands.length === 0 && services.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Super Admin Dashboard
          </h1>
          <p className="text-gray-600 mt-2">
            Manage brands, services, and brand service assignments
          </p>
        </div>

        {/* Error Display */}
        {currentError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{currentError}</p>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <IconComponent className="h-5 w-5" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "brands" && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">Brands</h2>
                <div className="flex gap-2">
                  <button
                    onClick={refreshBrands}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    disabled={brandLoading}
                  >
                    <FiRefreshCw
                      className={`h-4 w-4 ${
                        brandLoading ? "animate-spin" : ""
                      }`}
                    />
                    Refresh
                  </button>
                  <button
                    onClick={handleCreateBrand}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <FiPlus className="h-4 w-4" />
                    Add Brand
                  </button>
                </div>
              </div>
            </div>

            <div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Brand
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tenants
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {brands.map((brand) => {
                    const isExpanded = expandedBrands.has(brand.id);
                    const tenants = brandTenants[brand.id] || [];
                    const isFetchingTenants = loadingTenants.has(brand.id);

                    // Extract nested ternary for tenant count display
                    const getTenantCountDisplay = () => {
                      if (isFetchingTenants) return "Loading...";
                      if (isExpanded) return tenants.length.toString();
                      return "Click to view";
                    };

                    return (
                      <React.Fragment key={brand.id}>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <button
                                onClick={() =>
                                  handleToggleBrandExpansion(brand.id)
                                }
                                className="mr-2 p-1 hover:bg-gray-100 rounded transition-colors"
                              >
                                {isExpanded ? (
                                  <FiChevronDown className="h-4 w-4 text-gray-500" />
                                ) : (
                                  <FiChevronRight className="h-4 w-4 text-gray-500" />
                                )}
                              </button>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {brand.name}
                                </div>
                                {brand.description && (
                                  <div className="text-sm text-gray-500">
                                    {brand.description}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                brand.isActive
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {brand.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <FiUsers className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                {getTenantCountDisplay()}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(brand.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleEditBrand(brand)}
                                className="text-blue-600 hover:text-blue-900"
                                disabled={brandUpdating}
                              >
                                <FiEdit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleToggleBrandStatus(brand)}
                                className={`${
                                  brand.isActive
                                    ? "text-yellow-600 hover:text-yellow-900"
                                    : "text-green-600 hover:text-green-900"
                                }`}
                                disabled={brandUpdating}
                              >
                                {brand.isActive ? (
                                  <FiToggleRight className="h-4 w-4" />
                                ) : (
                                  <FiToggleLeft className="h-4 w-4" />
                                )}
                              </button>
                              <button
                                onClick={() => handleDeleteBrand(brand)}
                                className="text-red-600 hover:text-red-900"
                                disabled={brandDeleting}
                              >
                                <FiTrash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded tenants row */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={5} className="px-6 py-4 bg-gray-50">
                              <div className="space-y-2">
                                {isFetchingTenants ? (
                                  <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                    Loading tenants...
                                  </div>
                                ) : (
                                  <>
                                    {tenants.length > 0 ? (
                                      <>
                                        <div className="text-sm font-medium text-gray-700 mb-2">
                                          Tenants ({tenants.length})
                                        </div>
                                        <div className="grid gap-2">
                                          {tenants.map((tenant) => {
                                            // Get risk parameters for this tenant
                                            const tenantRiskParameters =
                                              riskParameters.filter(
                                                (rp) =>
                                                  rp.tenantId === tenant.id
                                              );

                                            // Get filtered and sorted risk parameters
                                            const filteredRiskParameters =
                                              getFilteredRiskParameters(
                                                tenantRiskParameters,
                                                tenant.id
                                              );

                                            // Get unique values for filter options
                                            const uniqueStatuses = [
                                              ...new Set(
                                                tenantRiskParameters.map(
                                                  (rp) => rp.responseStatus
                                                )
                                              ),
                                            ];
                                            const uniqueTypes = [
                                              ...new Set(
                                                tenantRiskParameters.map(
                                                  (rp) => rp.type
                                                )
                                              ),
                                            ];

                                            // Get current filters for this tenant
                                            const currentFilters =
                                              riskParameterFilters[tenant.id] ||
                                              {};

                                            return (
                                              <div
                                                key={tenant.id}
                                                className="p-3 bg-white rounded-md shadow-sm border"
                                              >
                                                <div className="flex items-center justify-between mb-3">
                                                  <div className="flex items-center gap-3">
                                                    <div className="flex-shrink-0">
                                                      <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                                                        <span className="text-blue-600 font-medium text-sm">
                                                          {tenant.name
                                                            .charAt(0)
                                                            .toUpperCase()}
                                                        </span>
                                                      </div>
                                                    </div>
                                                    <div>
                                                      <div className="text-sm font-medium text-gray-900">
                                                        {tenant.name}
                                                      </div>
                                                      {tenant.description && (
                                                        <div className="text-xs text-gray-500">
                                                          {tenant.description}
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                  <div className="flex items-center gap-2">
                                                    <span
                                                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                        tenant.isActive
                                                          ? "bg-green-100 text-green-800"
                                                          : "bg-red-100 text-red-800"
                                                      }`}
                                                    >
                                                      {tenant.isActive
                                                        ? "Active"
                                                        : "Inactive"}
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                      {new Date(
                                                        tenant.createdAt
                                                      ).toLocaleDateString()}
                                                    </span>
                                                  </div>
                                                </div>

                                                {/* Risk Parameters Section */}
                                                <div className="border-t pt-3">
                                                  <div className="flex items-center justify-between mb-2">
                                                    <button
                                                      onClick={() =>
                                                        handleToggleRiskParamsExpansion(
                                                          tenant.id
                                                        )
                                                      }
                                                      className="flex items-center gap-2 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors"
                                                    >
                                                      {expandedRiskParams.has(
                                                        tenant.id
                                                      ) ? (
                                                        <FiChevronDown className="h-3 w-3" />
                                                      ) : (
                                                        <FiChevronRight className="h-3 w-3" />
                                                      )}
                                                      Risk Parameters (
                                                      {
                                                        tenantRiskParameters.length
                                                      }
                                                      )
                                                      {currentFilters.status ||
                                                      currentFilters.type ? (
                                                        <span className="text-blue-600">
                                                          •{" "}
                                                          {
                                                            filteredRiskParameters.length
                                                          }{" "}
                                                          shown
                                                        </span>
                                                      ) : null}
                                                    </button>
                                                    <div className="flex items-center gap-1">
                                                      <select
                                                        onChange={(e) => {
                                                          const category =
                                                            e.target.value;
                                                          if (category) {
                                                            handleCreateRiskParameter(
                                                              tenant,
                                                              category
                                                            );
                                                            e.target.value = ""; // Reset dropdown
                                                          }
                                                        }}
                                                        className="text-xs border border-blue-200 rounded px-2 py-1 text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        value=""
                                                      >
                                                        {/* <option value="">+ Add Risk Parameter</option>
                                                        <option value="RISK_PARAMETERS">Risk Parameters</option>
                                                        <option value="DATA_QUALITY_CHECKS">Data Quality Checks</option> */}
                                                        <option value="">
                                                          + Add Risk Parameter
                                                        </option>
                                                        {Object.values(
                                                          MlQueryCategory
                                                        ).map((category) => (
                                                          <option
                                                            key={category}
                                                            value={category}
                                                          >
                                                            {category
                                                              .replace(
                                                                /_/g,
                                                                " "
                                                              )
                                                              .replace(
                                                                /\b\w/g,
                                                                (c) =>
                                                                  c.toUpperCase()
                                                              )}
                                                          </option>
                                                        ))}
                                                      </select>
                                                    </div>
                                                  </div>

                                                  {/* Filter Controls - shown when expanded */}
                                                  {expandedRiskParams.has(
                                                    tenant.id
                                                  ) &&
                                                    tenantRiskParameters.length >
                                                      0 && (
                                                      <div className="flex flex-wrap items-center gap-2 mb-3 p-2 bg-gray-50 rounded">
                                                        <div className="flex items-center gap-1">
                                                          <label className="text-xs font-medium text-gray-600">
                                                            Status:
                                                          </label>
                                                          <select
                                                            value={
                                                              currentFilters.status ||
                                                              "all"
                                                            }
                                                            onChange={(e) =>
                                                              updateRiskParameterFilter(
                                                                tenant.id,
                                                                "status",
                                                                e.target.value
                                                              )
                                                            }
                                                            className="text-xs border border-gray-300 rounded px-1 py-0.5"
                                                          >
                                                            <option value="all">
                                                              All
                                                            </option>
                                                            {uniqueStatuses.map(
                                                              (status) => (
                                                                <option
                                                                  key={status}
                                                                  value={status}
                                                                >
                                                                  {status}
                                                                </option>
                                                              )
                                                            )}
                                                          </select>
                                                        </div>

                                                        <div className="flex items-center gap-1">
                                                          <label className="text-xs font-medium text-gray-600">
                                                            Type:
                                                          </label>
                                                          <select
                                                            value={
                                                              currentFilters.type ||
                                                              "all"
                                                            }
                                                            onChange={(e) =>
                                                              updateRiskParameterFilter(
                                                                tenant.id,
                                                                "type",
                                                                e.target.value
                                                              )
                                                            }
                                                            className="text-xs border border-gray-300 rounded px-1 py-0.5"
                                                          >
                                                            <option value="all">
                                                              All
                                                            </option>
                                                            {uniqueTypes.map(
                                                              (type) => (
                                                                <option
                                                                  key={type}
                                                                  value={type}
                                                                >
                                                                  {type}
                                                                </option>
                                                              )
                                                            )}
                                                          </select>
                                                        </div>

                                                        <div className="flex items-center gap-1">
                                                          <label className="text-xs font-medium text-gray-600">
                                                            Sort:
                                                          </label>
                                                          <select
                                                            value={
                                                              currentFilters.sortBy ||
                                                              "created"
                                                            }
                                                            onChange={(e) =>
                                                              updateRiskParameterFilter(
                                                                tenant.id,
                                                                "sortBy",
                                                                e.target.value
                                                              )
                                                            }
                                                            className="text-xs border border-gray-300 rounded px-1 py-0.5"
                                                          >
                                                            <option value="created">
                                                              Created Date
                                                            </option>
                                                            <option value="status">
                                                              Status
                                                            </option>
                                                            <option value="type">
                                                              Type
                                                            </option>
                                                            <option value="description">
                                                              Description
                                                            </option>
                                                          </select>
                                                          <select
                                                            value={
                                                              currentFilters.sortOrder ||
                                                              "desc"
                                                            }
                                                            onChange={(e) =>
                                                              updateRiskParameterFilter(
                                                                tenant.id,
                                                                "sortOrder",
                                                                e.target.value
                                                              )
                                                            }
                                                            className="text-xs border border-gray-300 rounded px-1 py-0.5"
                                                          >
                                                            <option value="asc">
                                                              ↑ Asc
                                                            </option>
                                                            <option value="desc">
                                                              ↓ Desc
                                                            </option>
                                                          </select>
                                                        </div>
                                                      </div>
                                                    )}

                                                  {/* Collapsible Risk Parameters Content */}
                                                  {expandedRiskParams.has(
                                                    tenant.id
                                                  ) && (
                                                    <>
                                                      {filteredRiskParameters.length >
                                                      0 ? (
                                                        <div className="space-y-1">
                                                          {filteredRiskParameters.map(
                                                            (rp) => {
                                                              // Extract status class logic to reduce nesting
                                                              const getStatusClass =
                                                                (
                                                                  status: string
                                                                ) => {
                                                                  switch (
                                                                    status
                                                                  ) {
                                                                    case "SUCCESS":
                                                                      return "bg-green-100 text-green-700";
                                                                    case "FAILED":
                                                                      return "bg-red-100 text-red-700";
                                                                    case "IN_PROGRESS":
                                                                      return "bg-yellow-100 text-yellow-700";
                                                                    case "PENDING":
                                                                      return "bg-blue-100 text-blue-700";
                                                                    default:
                                                                      return "bg-gray-100 text-gray-700";
                                                                  }
                                                                };

                                                              return (
                                                                <div
                                                                  key={rp.id}
                                                                  className="flex items-center justify-between p-2 bg-white border rounded text-xs"
                                                                >
                                                                  <div className="flex-1 min-w-0">
                                                                    <div className="font-medium text-gray-700 truncate">
                                                                      {
                                                                        rp.description
                                                                      }
                                                                    </div>
                                                                    <div className="text-gray-500 truncate">
                                                                      {rp.query}
                                                                    </div>
                                                                    <div className="text-gray-400 text-xs mt-1 flex items-center gap-2">
                                                                      <span>
                                                                        Type:{" "}
                                                                        {
                                                                          rp.type
                                                                        }
                                                                      </span>
                                                                      <span>
                                                                        •
                                                                      </span>
                                                                      <span>
                                                                        {new Date(
                                                                          rp.createdAt
                                                                        ).toLocaleDateString()}
                                                                      </span>
                                                                    </div>
                                                                  </div>
                                                                  <div className="flex items-center gap-1 ml-2">
                                                                    <span
                                                                      className={`px-1 py-0.5 rounded text-xs ${getStatusClass(
                                                                        rp.responseStatus
                                                                      )}`}
                                                                    >
                                                                      {
                                                                        rp.responseStatus
                                                                      }
                                                                    </span>
                                                                    <button
                                                                      onClick={() =>
                                                                        handleEditRiskParameter(
                                                                          rp
                                                                        )
                                                                      }
                                                                      className="p-1 text-blue-600 hover:text-blue-800"
                                                                      title="Edit risk parameter"
                                                                    >
                                                                      <FiEdit className="h-3 w-3" />
                                                                    </button>
                                                                    <button
                                                                      onClick={() =>
                                                                        handleDeleteRiskParameterInline(
                                                                          rp.id
                                                                        )
                                                                      }
                                                                      className="p-1 text-red-600 hover:text-red-800"
                                                                      title="Delete risk parameter"
                                                                    >
                                                                      <FiTrash2 className="h-3 w-3" />
                                                                    </button>
                                                                  </div>
                                                                </div>
                                                              );
                                                            }
                                                          )}
                                                        </div>
                                                      ) : (
                                                        <div className="text-xs text-gray-400 text-center py-2">
                                                          {tenantRiskParameters.length ===
                                                          0
                                                            ? "No risk parameters defined"
                                                            : "No risk parameters match current filters"}
                                                        </div>
                                                      )}
                                                    </>
                                                  )}

                                                  {/* Collapsed view - show summary */}
                                                  {!expandedRiskParams.has(
                                                    tenant.id
                                                  ) &&
                                                    tenantRiskParameters.length >
                                                      0 && (
                                                      <div className="flex items-center gap-2 text-xs text-gray-500">
                                                        <span>
                                                          {
                                                            tenantRiskParameters.filter(
                                                              (rp) =>
                                                                rp.responseStatus ===
                                                                "SUCCESS"
                                                            ).length
                                                          }{" "}
                                                          Success,{" "}
                                                          {
                                                            tenantRiskParameters.filter(
                                                              (rp) =>
                                                                rp.responseStatus ===
                                                                "PENDING"
                                                            ).length
                                                          }{" "}
                                                          Pending,{" "}
                                                          {
                                                            tenantRiskParameters.filter(
                                                              (rp) =>
                                                                rp.responseStatus ===
                                                                "FAILED"
                                                            ).length
                                                          }{" "}
                                                          Failed
                                                        </span>
                                                      </div>
                                                    )}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </>
                                    ) : (
                                      <div className="text-sm text-gray-500 text-center py-4">
                                        No tenants found for this brand
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>

              {brands.length === 0 && (
                <div className="text-center py-12">
                  <BsFillBuildingFill className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No brands
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by creating a new brand.
                  </p>
                  <div className="mt-6">
                    <button
                      onClick={handleCreateBrand}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <FiPlus className="h-4 w-4 mr-2" />
                      Add Brand
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "services" && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">Services</h2>
                <div className="flex gap-2">
                  <button
                    onClick={refreshServices}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    disabled={servicesLoading}
                  >
                    <FiRefreshCw
                      className={`h-4 w-4 ${
                        servicesLoading ? "animate-spin" : ""
                      }`}
                    />
                    Refresh
                  </button>
                  <button
                    onClick={handleCreateService}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <FiPlus className="h-4 w-4" />
                    Add Service
                  </button>
                </div>
              </div>
            </div>

            <div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Service
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {services.map((service) => (
                    <tr key={service.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {service.name}
                          </div>
                          {service.description && (
                            <div className="text-sm text-gray-500">
                              {service.description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                          {service.code}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {service.category && (
                          <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                            {service.category}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            service.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {service.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEditService(service)}
                            className="text-blue-600 hover:text-blue-900"
                            disabled={serviceUpdating}
                          >
                            <FiEdit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggleServiceStatus(service)}
                            className={`${
                              service.isActive
                                ? "text-yellow-600 hover:text-yellow-900"
                                : "text-green-600 hover:text-green-900"
                            }`}
                            disabled={serviceUpdating}
                          >
                            {service.isActive ? (
                              <FiToggleRight className="h-4 w-4" />
                            ) : (
                              <FiToggleLeft className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteService(service)}
                            className="text-red-600 hover:text-red-900"
                            disabled={serviceDeleting}
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {services.length === 0 && (
                <div className="text-center py-12">
                  <MdMiscellaneousServices className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No services
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by creating a new service.
                  </p>
                  <div className="mt-6">
                    <button
                      onClick={handleCreateService}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <FiPlus className="h-4 w-4 mr-2" />
                      Add Service
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "brand-services" && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">
                  Brand Services
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={refreshBrandServices}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    disabled={brandServicesLoading}
                  >
                    <FiRefreshCw
                      className={`h-4 w-4 ${
                        brandServicesLoading ? "animate-spin" : ""
                      }`}
                    />
                    Refresh
                  </button>
                  <button
                    onClick={() => handleAssignService()}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <FiLink className="h-4 w-4" />
                    Assign Service
                  </button>
                </div>
              </div>
            </div>

            <div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Brand
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Service
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigned
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {brandServices.map((brandService) => (
                    <tr key={brandService.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {brandService.brand?.name || "Unknown Brand"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {brandService.service?.name || "Unknown Service"}
                          </div>
                          {brandService.service?.code && (
                            <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded inline-block mt-1">
                              {brandService.service.code}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            brandService.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {brandService.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(brandService.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() =>
                              handleToggleBrandServiceStatus(brandService)
                            }
                            className={`${
                              brandService.isActive
                                ? "text-yellow-600 hover:text-yellow-900"
                                : "text-green-600 hover:text-green-900"
                            }`}
                            disabled={brandServiceUpdating}
                          >
                            {brandService.isActive ? (
                              <FiToggleRight className="h-4 w-4" />
                            ) : (
                              <FiToggleLeft className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleUnassignService(brandService)}
                            className="text-red-600 hover:text-red-900"
                            disabled={brandServiceDeleting}
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {brandServices.length === 0 && (
                <div className="text-center py-12">
                  <FiLink className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No brand services
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by assigning services to brands.
                  </p>
                  <div className="mt-6">
                    <button
                      onClick={() => handleAssignService()}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <FiLink className="h-4 w-4 mr-2" />
                      Assign Service
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <BrandModal
        isOpen={brandModal.isOpen}
        onClose={() => setBrandModal({ isOpen: false, brand: null })}
        onSave={handleSaveBrand}
        brand={brandModal.brand}
        isLoading={brandCreating || brandUpdating}
      />

      <ServiceModal
        isOpen={serviceModal.isOpen}
        onClose={() => setServiceModal({ isOpen: false, service: null })}
        onSave={handleSaveService}
        service={serviceModal.service}
        isLoading={serviceCreating || serviceUpdating}
      />

      <AssignServiceModal
        isOpen={assignServiceModal.isOpen}
        onClose={() =>
          setAssignServiceModal({ isOpen: false, selectedBrand: null })
        }
        onAssign={handleSaveAssignment}
        brands={brands}
        availableServices={availableServices}
        selectedBrand={assignServiceModal.selectedBrand}
        isLoading={brandServiceCreating}
        onBrandChange={handleBrandChangeInModal}
      />

      <RiskParameterModal
        isOpen={riskParameterModal.isOpen}
        onClose={() => {
          setRiskParameterModal({ isOpen: false, riskParameter: null });
          setSelectedTenant(null);
          setSelectedCategory("RISK_PARAMETERS");
        }}
        onSave={handleSaveRiskParameter}
        onDelete={handleDeleteRiskParameter}
        onDeleteAll={handleDeleteAllRiskParameters}
        riskParameter={riskParameterModal.riskParameter}
        tenants={allTenants}
        selectedTenant={selectedTenant}
        selectedCategory={selectedCategory}
        isLoading={riskParameterCreating || riskParameterUpdating}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        isLoading={
          brandUpdating ||
          brandDeleting ||
          serviceUpdating ||
          serviceDeleting ||
          brandServiceUpdating ||
          brandServiceDeleting
        }
      />
    </div>
  );
};

export default SuperAdmin;
